import AccommodationSameRoomTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTile'
import AccommodationSameRoomTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileDetails'
import AccommodationSameRoomTileImage from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileImage'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import React, { useCallback, useContext, useMemo } from 'react'
import BedbankMultiSameRoomRoomOption from './BedbankMultiSameRoomRoomOption'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import uuidV4 from 'lib/string/uuidV4Utils'
import { rem } from 'polished'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import { smartScrollIntoViewIfNeeded } from 'lib/window/scrollUtils'
import { ROOM_MODE_TOGGLE_SECTION_ID } from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import ModalContext from 'contexts/ModalContext'
import AccommodationAmenitiesAndPoliciesModal from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesModal'
import type { RoomTypeAvailabilityStatus } from 'hooks/useBedbankPackageViews'
import useOfferPageBedbankRates from 'components/OfferPage/hooks/useOfferPageBedbankRates'
import { AccommodationSameRoomTileOptions } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileOptions'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'

interface Props {
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  rooms: Array<OfferPageRoom>
  tripPlannerTripId?: string
  fetchingRates?: boolean
  roomListId: string
  roomPosition: number
  youWereInterested?: boolean
  availabilityStatus: RoomTypeAvailabilityStatus
  isFilterActive: boolean
  ratesLayout: 'stacked' | 'carousel'
}

function BedbankMultiSameRoomRoom({
  packageView,
  checkInDate,
  checkOutDate,
  rooms,
  fetchingRates,
  roomListId,
  roomPosition,
  youWereInterested,
  availabilityStatus,
  isFilterActive,
  ratesLayout,
}: Props) {
  const { rates, isFlexCfmrBedbankPackagesEnabled } = useOfferPageBedbankRates(packageView.effectiveRates, checkInDate, checkOutDate)

  const hasPromotions = !!packageView.promotions.length
  const hasMobileOnlyPrice = useMemo(() => {
    return rates.some(rate => rate.mobilePromotion)
  }, [rates])
  const roomCount = rooms.length
  const validCheckinCheckout = !!(checkInDate && checkOutDate)

  const hotelAvailable = packageView.available
  const canPurchase = hotelAvailable && rates.length > 0 && packageView.capacityValid && !fetchingRates

  // use full rates as dep so when user expand/hide rates the list id stay the same
  const rateListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rates, canPurchase])

  const offerPageDispatch = useOfferPageDispatch()

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, packageView.package.roomId])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, packageView.package.roomId])

  const onSelectIndividualRoomMode = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
    const toggleElement = document.getElementById(ROOM_MODE_TOGGLE_SECTION_ID)
    if (toggleElement) {
      smartScrollIntoViewIfNeeded(toggleElement)
    }
  }, [offerPageDispatch])

  const showModal = useContext(ModalContext)

  const onShowRoomDetails = useCallback(() => {
    if (packageView) {
      showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={packageView.package} />)
    }
  }, [packageView, showModal])

  const carouselWidth = rates.length > 1 ? rem(300) : '100%'

  const roomTileActionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (fetchingRates) {
      return 'loading'
    }
    if (!packageView.capacityValid) {
      return 'exceeded-capacity'
    }
    if (!canPurchase) {
      if (availabilityStatus === 'partiallyAvailable') {
        return 'available-for-some-rooms'
      }
      if (validCheckinCheckout && packageView.capacityValid) {
        return 'sold-out-on-dates'
      }
    }
    if (validCheckinCheckout && !hotelAvailable && packageView.capacityValid) {
      return 'sold-out'
    }
    if (!isFilterActive && rates.length === 0 && checkInDate && checkOutDate) {
      return 'sold-out'
    }
    if (hotelAvailable && packageView.capacityValid && !validCheckinCheckout) {
      return 'available-on-dates'
    }
  }, [canPurchase, checkInDate, checkOutDate, fetchingRates, hotelAvailable, isFilterActive, packageView.capacityValid, rates.length, validCheckinCheckout, availabilityStatus])

  const packageOptions = rates.map((rate, index) => <BedbankMultiSameRoomRoomOption
    key={rate.id}
    rate={rate}
    packageNumber={index + 1}
    packageView={packageView}
    isFlexCfmrBedbankPackagesEnabled={isFlexCfmrBedbankPackagesEnabled}
    roomPosition={roomPosition}
    roomListId={roomListId}
    rateListId={rateListId}
  />)

  return <div data-testid="multi-same-room-room">
    {youWereInterested && <MessageBanner
      kind="promotional-primary"
      icon={<LineSightseeingIcon />}
    >
      <b>You were interested in this option</b>
    </MessageBanner>}
    <AccommodationSameRoomTile roomPackage={packageView.package}>
      <AccommodationSameRoomTileImage
        roomPackage={packageView.package}
      />
      <AccommodationSameRoomTileDetails
        roomPackage={packageView.package}
        roomCount={roomCount}
        urgencyLabels={<>
          {hasPromotions && <BookingsCountLabel
            bookings={packageView.package.stats.booked}
            threshold="room"
          />}
          {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
        </>}
        onShowRoomDetails={onShowRoomDetails}
      >
        <LoadingIndicator visible={fetchingRates} floating opaque />
        <AccommodationRoomTileActions
          roomPackage={packageView.package}
          state={roomTileActionsState}
          onChangeOccupancyClick={handleCheckAvailabilityClick}
          onChangeDatesClick={handleChangeDatesClick}
          onCheckAvailabilityClick={handleCheckAvailabilityClick}
          onMultiRoomModeClick={onSelectIndividualRoomMode}
        />
      </AccommodationSameRoomTileDetails>
      <AccommodationSameRoomTileOptions>
        {ratesLayout === 'stacked' && packageOptions}
        {ratesLayout === 'carousel' && rates.length > 0 &&
          <CardCarousel width={carouselWidth} gutterSize={20} gap={12} snap="start">
            {packageOptions}
          </CardCarousel>}
      </AccommodationSameRoomTileOptions>
    </AccommodationSameRoomTile>
  </div>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'isFilterActive'
>

export default withOfferPageState((offerState): MappedProps => ({
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  rooms: offerState.rooms,
  isFilterActive: !!offerState.activeFilter,
}), BedbankMultiSameRoomRoom)
